import { ArticlesState } from 'mk2/apps/blogs/containers/Article/Article.reducers';
import * as fromBlogsComments from 'mk2/apps/blogs/containers/Comments/Comments.reducers';
import { State as GroupBlockedMembersState } from 'mk2/apps/blogs/containers/GroupBlockedMembers/GroupBlockedMembers.reducers';
import { State as GroupBlockMemberState } from 'mk2/apps/blogs/containers/GroupBlockMember/GroupBlockMember.reducers';
import { State as GroupCreateState } from 'mk2/apps/blogs/containers/GroupCreate/GroupCreate.reducers';
import { State as GroupEditState } from 'mk2/apps/blogs/containers/GroupEdit/GroupEdit.reducers';
import { State as GroupFeedsState } from 'mk2/apps/blogs/containers/GroupFeed/GroupFeed.reducers';
import { State as GroupInfoState } from 'mk2/apps/blogs/containers/GroupInfo/GroupInfo.reducers';
import { State as GroupInvitationState } from 'mk2/apps/blogs/containers/GroupInvitation/GroupInvitation.reducers';
import { State as GroupInvitedMembersState } from 'mk2/apps/blogs/containers/GroupInvitedMembers/GroupInvitedMembers.reducers';
import { State as GroupMembersState } from 'mk2/apps/blogs/containers/GroupMembers/GroupMembers.reducers';
import { State as GroupPhotosState } from 'mk2/apps/blogs/containers/GroupPhotos/GroupPhotos.reducers';
import { State as GroupPostDetailState } from 'mk2/apps/blogs/containers/GroupPostDetail/GroupPostDetail.reducers';
import { State as GroupPostEditsState } from 'mk2/apps/blogs/containers/GroupPostEdit/GroupPostEdit.reducers';
import { State as GroupSharedState } from 'mk2/apps/blogs/containers/GroupShared/GroupShared.reducers';
import * as fromLikeInfoPopup from 'mk2/apps/blogs/containers/LikeInfoPopup/LikeInfoPopup.reducers';
import * as fromLikeSection from 'mk2/apps/blogs/containers/LikeSection/LikeSection.reducers';
import { State as ListOfGroupsState } from 'mk2/apps/blogs/containers/ListOfGroups/ListOfGroups.reducers';
import { State as PhotoDetailState } from 'mk2/apps/blogs/containers/PhotoDetail/PhotoDetail.reducers';
import * as fromBlogsUserStrollers from 'mk2/apps/blogs/containers/UserStrollersWidget/UserStrollersWidget.reducers';
import { CommentEntity, CommentSchema } from 'mk2/apps/blogs/schemas';
import { AppState } from 'mk2/reducers';
import { createDenormalizedEntitiesSelector } from 'mk2/selectors';
import { combineReducers } from 'redux';
import { createSelector } from 'reselect';
import {
    getCanAdd,
    getErrorMessage,
    getIds,
    getIdsOrNull,
    getLoadingState,
} from './containers/Comments/Comments.selectors';

interface BlogsSharedState {
    comments: fromBlogsComments.PostsState;
    likeInfoPopups: fromLikeInfoPopup.LikeInfoPopupsState;
    likeSections: fromLikeSection.LikeSectionsState;
    userStrollers: fromBlogsUserStrollers.UserStrollersState;
}

const blogsSharedReducer = combineReducers<BlogsSharedState>({
    comments: fromBlogsComments.postsReducer,
    likeInfoPopups: fromLikeInfoPopup.likeInfoPopupsReducer,
    likeSections: fromLikeSection.likeSectionsReducer,
    userStrollers: fromBlogsUserStrollers.userStrollersReducer,
});

export interface BlogsAppState {
    shared: BlogsSharedState;
    blogsArticles: ArticlesState;
    groupCreate: GroupCreateState;
    groupFeed: GroupFeedsState;
    groupPostDetail: GroupPostDetailState;
    groupPostEdits: GroupPostEditsState;
    listOfGroups: ListOfGroupsState;
    groupInfo: GroupInfoState;
    groupPhotos: GroupPhotosState;
    groupEdit: GroupEditState;
    groupBlockedMembers: GroupBlockedMembersState;
    groupInvitedMembers: GroupInvitedMembersState;
    groupInvitation: GroupInvitationState;
    groupBlockMember: GroupBlockMemberState;
    groupMembers: GroupMembersState;
    groupShared: GroupSharedState;
    photoDetail: PhotoDetailState;
}

export const blogsReducer = {
    shared: blogsSharedReducer,
};

const getDenormalizedComments = createDenormalizedEntitiesSelector<CommentEntity>();

// Export selectors for Comments in blogs
export const getCommentsIds = (state: AppState, key: string) => getIds(state.containers.blogs.shared.comments, key);
export const getCommentsIdsOrNull = (state: AppState, key: string) => getIdsOrNull(state.containers.blogs.shared.comments, key);
export const getCommentsComments = (state: AppState, key: string) => getDenormalizedComments(state, CommentSchema, getIds(state.containers.blogs.shared.comments, key));
export const getCommentsLoadingState = (state: AppState, key: string) => getLoadingState(state.containers.blogs.shared.comments, key);
export const getCommentsErrorMessage = (state: AppState, key: string) => getErrorMessage(state.containers.blogs.shared.comments, key);
export const getCommentsCanAdd = (state: AppState, key: string) => getCanAdd(state.containers.blogs.shared.comments, key);

export const getCommentsNonDeletedComments = createSelector(
    [
        (state: AppState) => state,
        (state: AppState, key: string) => key,
    ],
    (state: AppState, key: string) => {
        return getCommentsComments(state, key).filter((comment) => !comment.deletedAt);
    },
);

// Export selectors for LikeInfoPopup in blogs
export const getLikeInfoPopupState = (state: AppState, entityType: string, entityId: number): fromLikeInfoPopup.LikeInfoPopupState =>
    state.containers.blogs.shared.likeInfoPopups[`${entityType}/${entityId}`] || fromLikeInfoPopup.initialLikeInfoPopupState;

// Export selectors for LikeSection in blogs
export const getLikeSectionState = (state: AppState, entityType: string, entityId: number): fromLikeSection.LikeSectionState =>
    state.containers.blogs.shared.likeSections[`${entityType}/${entityId}`] || fromLikeSection.initialLikeSectionState;

// Export selectors for UserStrollersWidget in blogs
export const getBlogsUserStrollerState = (state: AppState): fromBlogsUserStrollers.UserStrollersState =>
    state.containers.blogs.shared.userStrollers;
